import { useCallback, useRef, useState } from 'react';

import { getAmaConversation, streamAmaChat } from '../../services/api';

const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const emptyAssistant = () => ({
	id: makeId(),
	role: 'assistant',
	content: '',
	toolCalls: [],
	error: null,
});

const hydrateFromServer = (msgs) =>
	(msgs || []).map((m) => ({
		id: `db-${m.id}`,
		role: m.role,
		content: m.content || '',
		toolCalls: (m.tool_calls || []).map((tc) => ({
			name: tc.name,
			args: tc.args,
			status: tc.ok === true ? 'ok' : tc.ok === false ? 'error' : 'ok',
		})),
		error: null,
	}));

export default function useAmaStream() {
	const [messages, setMessages] = useState([]);
	const [conversationId, setConversationId] = useState(null);
	const [isStreaming, setIsStreaming] = useState(false);
	const abortRef = useRef(null);

	const loadConversation = useCallback(async (id) => {
		if (abortRef.current) abortRef.current.abort();
		abortRef.current = null;
		setIsStreaming(false);
		if (!id) {
			setMessages([]);
			setConversationId(null);
			return;
		}
		try {
			const data = await getAmaConversation(id);
			setMessages(hydrateFromServer(data.messages));
			setConversationId(data.id);
		} catch (e) {
			setMessages([]);
			setConversationId(null);
		}
	}, []);

	const startNew = useCallback(() => {
		if (abortRef.current) abortRef.current.abort();
		abortRef.current = null;
		setMessages([]);
		setConversationId(null);
		setIsStreaming(false);
	}, []);

	const send = useCallback(async (text, options = {}) => {
		const { fresh = false } = options;
		const trimmed = (text || '').trim();
		if (!trimmed || isStreaming) return;

		if (fresh && abortRef.current) {
			abortRef.current.abort();
			abortRef.current = null;
		}

		const userMsg = { id: makeId(), role: 'user', content: trimmed };
		const assistantMsg = emptyAssistant();

		if (fresh) {
			setMessages([userMsg, assistantMsg]);
			setConversationId(null);
		} else {
			setMessages((prev) => [...prev, userMsg, assistantMsg]);
		}
		setIsStreaming(true);

		const controller = new AbortController();
		abortRef.current = controller;

		const historyForApi = (() => {
			const base = fresh ? [] : messages;
			const all = [...base, userMsg];
			return all.map(({ role, content }) => ({ role, content }));
		})();

		const updateAssistant = (mutator) => {
			setMessages((prev) => {
				const idx = prev.findIndex((m) => m.id === assistantMsg.id);
				if (idx === -1) return prev;
				const next = [...prev];
				next[idx] = mutator({ ...next[idx] });
				return next;
			});
		};

		let convIdForThisCall = fresh ? null : conversationId;

		try {
			await streamAmaChat(
				historyForApi,
				(event) => {
					if (event.type === 'conversation') {
						if (event.id && event.id !== convIdForThisCall) {
							convIdForThisCall = event.id;
							setConversationId(event.id);
						}
					} else if (event.type === 'text') {
						updateAssistant((m) => ({ ...m, content: m.content + (event.delta || '') }));
					} else if (event.type === 'tool_call') {
						updateAssistant((m) => ({
							...m,
							toolCalls: [...m.toolCalls, { name: event.name, args: event.args, status: 'running' }],
						}));
					} else if (event.type === 'tool_result') {
						updateAssistant((m) => {
							const toolCalls = [...m.toolCalls];
							for (let i = toolCalls.length - 1; i >= 0; i--) {
								if (toolCalls[i].name === event.name && toolCalls[i].status === 'running') {
									toolCalls[i] = { ...toolCalls[i], status: event.ok ? 'ok' : 'error' };
									break;
								}
							}
							return { ...m, toolCalls };
						});
					} else if (event.type === 'error') {
						updateAssistant((m) => ({ ...m, error: event.message || 'unknown error' }));
					}
				},
				controller.signal,
				convIdForThisCall,
			);
		} catch (e) {
			if (e.name !== 'AbortError') {
				updateAssistant((m) => ({ ...m, error: e.message || String(e) }));
			}
		} finally {
			setIsStreaming(false);
			abortRef.current = null;
		}
	}, [messages, isStreaming, conversationId]);

	const stop = useCallback(() => {
		if (abortRef.current) {
			abortRef.current.abort();
			abortRef.current = null;
		}
	}, []);

	return { messages, conversationId, isStreaming, send, stop, loadConversation, startNew };
}
